import { truncateLegendLabel } from './nivoTheme';

type LegendItem = { id: string | number; label: string; color: string };

type WrappedLegendOptions = {
    // Actual measured width of the chart (e.g. from useElementWidth) - when given,
    // itemsPerRow is derived from it instead of the fixed default, so the legend wraps
    // to fit instead of overflowing/clipping past the SVG edge on narrow screens.
    containerWidth?: number;
    itemsPerRow?: number;
    itemWidth?: number;
    itemHeight?: number;
    rowGap?: number;
    startY?: number;
    symbolSize?: number;
    maxLabelLength?: number;
    onMouseEnter?: (datum: { id: string | number }, event: unknown) => void;
    onMouseLeave?: (datum: { id: string | number }, event: unknown) => void;
};

// Vertical space a single-line axis title needs right after the tick-label zone,
// before a legend row (or the chart's bottom edge) can start without overlapping it.
export const AXIS_TITLE_HEIGHT = 22;

// Chart.js's legend auto-wraps to fit its container; Nivo lays a single legend out as
// one fixed-width row that never wraps, so it overflows/clips past the SVG edge once
// it's wider than the chart. This chunks legend items into several stacked row-legends
// (Nivo's `legends` prop accepts several) sized to the chart's actual width via
// `containerWidth`, so it wraps like a normal responsive legend instead.
export function buildWrappedLegends(items: LegendItem[], opts: WrappedLegendOptions = {}) {
    const {
        containerWidth,
        itemWidth = 170,
        itemHeight = 20,
        rowGap = 4,
        startY = 40,
        symbolSize = 12,
        maxLabelLength = 22,
        onMouseEnter,
        onMouseLeave
    } = opts;

    const itemsPerRow = opts.itemsPerRow ?? (containerWidth ? Math.max(1, Math.floor(containerWidth / itemWidth)) : 4);

    const rows: LegendItem[][] = [];
    for (let i = 0; i < items.length; i += itemsPerRow) {
        rows.push(items.slice(i, i + itemsPerRow));
    }

    const legends = rows.map((rowItems, rowIndex) => ({
        dataFrom: 'keys' as const,
        anchor: 'bottom' as const,
        direction: 'row' as const,
        translateY: startY + rowIndex * (itemHeight + rowGap),
        itemWidth,
        itemHeight,
        symbolSize,
        data: rowItems.map((item) => ({ id: item.id, label: truncateLegendLabel(item.label, maxLabelLength), color: item.color })),
        onMouseEnter,
        onMouseLeave
    }));

    return { legends, rowCount: rows.length };
}

// Total bottom margin needed to fit `rowCount` stacked legend rows below the axis area.
export function legendMarginBottom(rowCount: number, opts: Pick<WrappedLegendOptions, 'itemHeight' | 'rowGap' | 'startY'> = {}) {
    const { itemHeight = 20, rowGap = 4, startY = 40 } = opts;
    return startY + Math.max(rowCount, 1) * (itemHeight + rowGap) + 10;
}
